import { useId, type ReactNode } from "react";
import { StatusBadge, type StatusLevel } from "../StatusBadge.tsx";

/**
 * StatusGrid — dense status cells (observability-ui task 1.4; design §Components and Interfaces #3,
 * reference "Nodes": a live list of nodes/services with key info, adapted natively — no Netdata/GPL
 * code copied).
 *
 * A compact grid of cells, each a node/service/room analogue: its name, a {@link StatusBadge}
 * (status by glyph + text, never colour alone — design 12.4, Property 4), and a short list of
 * labelled metrics. Where a cell has a detail route (`href`), the whole cell is a click-through
 * link; otherwise it renders as a static region. Either way the cell is reachable and operable by
 * keyboard — a link is natively focusable/activatable, and a static cell is announced as a listitem.
 *
 * It is presentational and screen-agnostic (design §Architecture): props in, no data fetching and no
 * routing of its own. A screen maps its existing read into {@link StatusGridItem}s; a cell's `href`
 * is whatever detail route that screen owns.
 *
 * Honest states (Requirement 1.2, Property 2): with no items it shows an explicit empty/loading
 * affordance rather than an empty box or fabricated cells. Every colour comes from the theme tokens
 * via `observability.css` (Requirement 2.1, 2.3, Property 1).
 */

/** A single labelled metric shown inside a status cell, e.g. `{ label: "p95", value: "128 ms" }`. */
export interface StatusGridMetric {
  /** Short metric name, shown de-emphasised. */
  readonly label: string;
  /** The metric value as display text (already formatted by the caller — no fabrication here). */
  readonly value: string;
}

/** One cell in the grid: a node/service/room analogue. */
export interface StatusGridItem {
  /** Stable id (used as the React key). */
  readonly id: string;
  /** Human name of the node/service, shown as the cell's heading. */
  readonly name: string;
  /** Status level; rendered via {@link StatusBadge} (glyph + text, never colour alone). */
  readonly status: StatusLevel;
  /** Optional per-level wording for the badge (defaults to the badge's own label). */
  readonly statusLabel?: string;
  /** The key metrics for this cell, shown as text. Omit/empty for a status-only cell. */
  readonly metrics?: readonly StatusGridMetric[];
  /**
   * Optional click-through to this item's detail route. When present the whole cell is a link
   * (natively keyboard-focusable/activatable); when absent the cell is a static listitem.
   */
  readonly href?: string;
}

export interface StatusGridProps {
  /** Accessible name for the region/heading. Defaults to "Status". */
  readonly title?: string;
  /** The cells, in display order (callers pre-order their rows). */
  readonly items: readonly StatusGridItem[];
  /** Render the loading affordance instead of the grid (a read is still in flight). */
  readonly loading?: boolean;
  /** Message for the empty state when there are no items and not loading. */
  readonly emptyMessage?: string;
}

export function StatusGrid(props: StatusGridProps): ReactNode {
  const labelId = useId();
  const title = props.title ?? "Status";
  const isLoading = props.loading === true;
  const isEmpty = !isLoading && props.items.length === 0;

  return (
    <section className="obs-panel obs-statusgrid" aria-labelledby={labelId}>
      <div className="obs-panel__header">
        <h3 id={labelId} className="obs-panel__title">
          {title}
        </h3>
      </div>

      {isLoading ? (
        <div className="obs-state" data-kind="loading" role="status" aria-live="polite">
          <strong>Loading…</strong>
          <span className="obs-state__hint">Fetching current status.</span>
        </div>
      ) : isEmpty ? (
        <div className="obs-state" data-kind="empty">
          <strong>Nothing to show</strong>
          <span className="obs-state__hint">
            {props.emptyMessage ?? "No items are reporting status right now."}
          </span>
        </div>
      ) : (
        <ul className="obs-statusgrid__list" role="list">
          {props.items.map((item) => (
            <li key={item.id} className="obs-statusgrid__item">
              <StatusCell item={item} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * A single status cell. When the item has an `href` the cell is an anchor (a native keyboard-
 * focusable, activatable click-through); otherwise it is a plain div. The inner markup is identical
 * so the two read the same visually and to assistive tech.
 */
function StatusCell(props: { item: StatusGridItem }): ReactNode {
  const { item } = props;
  const body = (
    <>
      <div className="obs-statusgrid__head">
        <span className="obs-statusgrid__name">{item.name}</span>
        <StatusBadge level={item.status} label={item.statusLabel} size="sm" />
      </div>
      {item.metrics !== undefined && item.metrics.length > 0 && (
        <dl className="obs-statusgrid__metrics">
          {item.metrics.map((m, i) => (
            <div key={`${m.label}-${i}`} className="obs-statusgrid__metric">
              <dt className="obs-statusgrid__metric-label">{m.label}</dt>
              <dd className="obs-statusgrid__metric-value">{m.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </>
  );

  if (item.href !== undefined) {
    return (
      <a
        className="obs-statusgrid__cell"
        href={item.href}
        data-interactive="true"
        aria-label={`${item.name} — open details`}
      >
        {body}
      </a>
    );
  }
  return <div className="obs-statusgrid__cell">{body}</div>;
}
